import { z } from "zod";

export const forgotPasswordRequestFormSchema = z.object({
  email: z
    .string()
    .email({ message: "Please enter a valid email." })
    .nonempty(),
});

export const loginFormSchema = z.object({
  email: z
    .string()
    .email({ message: "Please enter a valid email." })
    .nonempty(),
  password: z.string().nonempty({ message: "Please enter your password." }),
  org: z.string(),
});

export const updatePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, { message: "Please enter a password." }),
    newPassword: z
      .string()
      .min(8, { message: "Password must be at least 8 characters." })
      .regex(/^(?=.*[A-Z])(?=.*[a-z])(?=.*[^A-Za-z0-9]).{8,}$/, {
        message:
          "Password must contain at least one uppercase letter, one lowercase letter and a special character.",
      }),
    confirmNewPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: "Passwords do not match.",
    path: ["confirmNewPassword"],
  })
  .refine((data) => data.oldPassword !== data.newPassword, {
    message: "New password cannot be the same as your current password.",
    path: ["newPassword"],
  });

export const addAccountFormSchema = z
  .object({
    email: z.string().email().min(1),
    verifyEmail: z.string().email().min(1),
  })
  .refine((data) => data.email === data.verifyEmail, {
    message: "Emails do not match.",
    path: ["verifyEmail"],
  });

export const resetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .nonempty({ message: "Please enter a password." })
      .min(8, { message: "Password must be at least 8 characters." })
      .regex(/^(?=.*[A-Z])(?=.*[a-z])(?=.*[^A-Za-z0-9]).{8,}$/, {
        message:
          "Password must contain at least one uppercase letter, one lowercase letter and a special character.",
      }),
    confirmNewPassword: z
      .string()
      .nonempty({ message: "Please re-enter new password." }),
    id: z.string().nonempty(),
    token: z.string().nonempty(),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: "Passwords do not match.",
    path: ["confirmNewPassword"],
  });

export type LoginFormData = z.infer<typeof loginFormSchema>;
export type ForgotPasswordRequestData = z.infer<
  typeof forgotPasswordRequestFormSchema
>;
export type UpdatePasswordData = z.infer<typeof updatePasswordSchema>;
export type AddAccountFormData = z.infer<typeof addAccountFormSchema>;
export type ResetPasswordData = z.infer<typeof resetPasswordSchema>;
